import { useState, useMemo } from 'react';
import {
  Search,
  Tag,
  Heading,
  Code2,
  Smartphone,
  Globe,
  FileText,
  Link2,
  Zap,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Info,
  ExternalLink,
  Loader,
} from 'lucide-react';
import type { PageResult, SeoCategoryScore, SeoIssue, SeoCategory, SeoIssueSeverity } from '../../types';

interface SeoTabProps {
  pageResults: PageResult[];
  loading?: boolean;
}

// Category configuration with icons and descriptions
const CATEGORY_CONFIG: Record<SeoCategory, { label: string; description: string; icon: React.ElementType }> = {
  meta_tags: {
    label: 'Meta Tags',
    description: 'Title, description, viewport, robots',
    icon: Tag,
  },
  headings: {
    label: 'Headings Structure',
    description: 'H1-H6 hierarchy and organization',
    icon: Heading,
  },
  structured_data: {
    label: 'Structured Data',
    description: 'JSON-LD and schema.org markup',
    icon: Code2,
  },
  mobile: {
    label: 'Mobile Friendliness',
    description: 'Viewport and mobile optimization',
    icon: Smartphone,
  },
  crawlability: {
    label: 'Crawlability',
    description: 'Canonical URLs, robots, indexing',
    icon: Globe,
  },
  content: {
    label: 'Content Quality',
    description: 'Word count, images, text-to-HTML ratio',
    icon: FileText,
  },
  links: {
    label: 'Internal Linking',
    description: 'Anchor text, link structure',
    icon: Link2,
  },
  performance: {
    label: 'Performance',
    description: 'Core Web Vitals impact',
    icon: Zap,
  },
};

function getScoreColor(score: number): string {
  if (score >= 90) return 'text-status-success';
  if (score >= 70) return 'text-status-warning';
  if (score >= 50) return 'text-orange-500';
  return 'text-status-error';
}

function getScoreBgColor(score: number): string {
  if (score >= 90) return 'bg-status-success';
  if (score >= 70) return 'bg-status-warning';
  if (score >= 50) return 'bg-orange-500';
  return 'bg-status-error';
}

function getSeverityIcon(severity: SeoIssueSeverity) {
  switch (severity) {
    case 'critical':
      return <AlertCircle size={16} className="text-status-error" />;
    case 'warning':
      return <AlertTriangle size={16} className="text-status-warning" />;
    case 'info':
      return <Info size={16} className="text-blue-500" />;
  }
}

function getSeverityBadgeClasses(severity: SeoIssueSeverity): string {
  switch (severity) {
    case 'critical':
      return 'bg-status-error/10 text-status-error';
    case 'warning':
      return 'bg-status-warning/10 text-status-warning';
    case 'info':
      return 'bg-blue-500/10 text-blue-500';
  }
}

// Circular gauge component
function CircularGauge({ score, size = 140 }: { score: number; size?: number }) {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-light"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={`transition-all duration-700 ${score >= 90 ? 'stroke-status-success' : score >= 70 ? 'stroke-status-warning' : score >= 50 ? 'stroke-orange-500' : 'stroke-status-error'}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-display font-bold ${getScoreColor(score)}`}>
          {score}
        </span>
        <span className="text-caption text-gray-medium">/ 100</span>
      </div>
    </div>
  );
}

// Category card component
function CategoryCard({
  categoryScore,
  isExpanded,
  onToggle,
  issues,
}: {
  categoryScore: SeoCategoryScore;
  isExpanded: boolean;
  onToggle: () => void;
  issues: SeoIssue[];
}) {
  const config = CATEGORY_CONFIG[categoryScore.category];
  const Icon = config.icon;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-card ${categoryScore.score >= 90 ? 'bg-status-success/10' : categoryScore.score >= 70 ? 'bg-status-warning/10' : 'bg-status-error/10'}`}>
            <Icon size={24} className={getScoreColor(categoryScore.score)} />
          </div>
          <div>
            <h4 className="text-body font-semibold">{config.label}</h4>
            <p className="text-caption text-gray-medium">{config.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-6">
          {/* Issue counts */}
          <div className="flex items-center gap-3 text-body-sm">
            {categoryScore.criticalCount > 0 && (
              <span className="flex items-center gap-1 text-status-error">
                <AlertCircle size={14} />
                {categoryScore.criticalCount}
              </span>
            )}
            {categoryScore.warningCount > 0 && (
              <span className="flex items-center gap-1 text-status-warning">
                <AlertTriangle size={14} />
                {categoryScore.warningCount}
              </span>
            )}
            {categoryScore.infoCount > 0 && (
              <span className="flex items-center gap-1 text-blue-500">
                <Info size={14} />
                {categoryScore.infoCount}
              </span>
            )}
          </div>
          {/* Score */}
          <div className="flex items-center gap-3">
            <div className="w-16">
              <div className="h-2 bg-gray-light rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${getScoreBgColor(categoryScore.score)}`}
                  style={{ width: `${categoryScore.score}%` }}
                />
              </div>
            </div>
            <span className={`text-body font-semibold w-10 text-right ${getScoreColor(categoryScore.score)}`}>
              {categoryScore.score}
            </span>
          </div>
          {isExpanded ? (
            <ChevronDown size={20} className="text-gray-medium" />
          ) : (
            <ChevronRight size={20} className="text-gray-medium" />
          )}
        </div>
      </button>

      {/* Expanded issues list */}
      {isExpanded && issues.length > 0 && (
        <div className="border-t border-gray-light">
          {issues.map((issue) => (
            <IssueItem key={issue.id} issue={issue} />
          ))}
        </div>
      )}
      {isExpanded && issues.length === 0 && (
        <div className="border-t border-gray-light p-4 text-center text-gray-medium">
          No issues found in this category
        </div>
      )}
    </div>
  );
}

// Individual issue item
function IssueItem({ issue }: { issue: SeoIssue }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-b border-gray-light last:border-b-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-3 p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        {getSeverityIcon(issue.severity)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-body font-medium text-dark-text">
              {issue.title}
            </span>
            <span className={`px-2 py-0.5 rounded text-caption font-medium ${getSeverityBadgeClasses(issue.severity)}`}>
              {issue.severity}
            </span>
          </div>
          {!isExpanded && (
            <p className="text-body-sm text-gray-medium mt-1 line-clamp-1">
              {issue.description}
            </p>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown size={16} className="text-gray-medium flex-shrink-0 mt-1" />
        ) : (
          <ChevronRight size={16} className="text-gray-medium flex-shrink-0 mt-1" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pl-10 space-y-3">
          <p className="text-body-sm text-gray-medium">
            {issue.description}
          </p>

          <div className="p-3 bg-status-warning/5 rounded-card border border-status-warning/20">
            <p className="text-body-sm font-medium text-status-warning mb-1">Impact</p>
            <p className="text-body-sm text-gray-medium">{issue.impact}</p>
          </div>

          <div className="p-3 bg-status-success/5 rounded-card border border-status-success/20">
            <p className="text-body-sm font-medium text-status-success mb-1">Recommendation</p>
            <p className="text-body-sm text-gray-medium">{issue.recommendation}</p>
          </div>

          {issue.element && (
            <div className="p-3 bg-gray-light/50 rounded-card">
              <p className="text-caption font-medium text-gray-medium mb-1">Affected Element</p>
              <code className="text-caption text-dark-text break-all">
                {issue.element}
              </code>
            </div>
          )}

          {issue.googleDocsUrl && (
            <a
              href={issue.googleDocsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-body-sm text-coral hover:text-coral/80"
            >
              Google Search Documentation
              <ExternalLink size={14} />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card text-center py-12">
      <Search size={48} className="mx-auto text-gray-medium opacity-50 mb-4" />
      <h3 className="text-heading-3 font-semibold mb-2">No SEO Data Available</h3>
      <p className="text-body text-gray-medium max-w-md mx-auto">
        SEO analysis was not performed during this scan. Run a new scan with SEO enabled to see detailed SEO recommendations.
      </p>
    </div>
  );
}

export function SeoTab({ pageResults, loading }: SeoTabProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<SeoCategory>>(new Set());
  const [filterSeverity, setFilterSeverity] = useState<SeoIssueSeverity | 'all'>('all');

  // Aggregate SEO results from all pages
  const aggregatedData = useMemo(() => {
    const pagesWithSeo = pageResults.filter((p) => p.seoResult);

    if (pagesWithSeo.length === 0) {
      return null;
    }

    // Aggregate scores across all pages
    const categoryTotals: Record<SeoCategory, { totalScore: number; count: number; issues: SeoIssue[] }> = {
      meta_tags: { totalScore: 0, count: 0, issues: [] },
      headings: { totalScore: 0, count: 0, issues: [] },
      structured_data: { totalScore: 0, count: 0, issues: [] },
      mobile: { totalScore: 0, count: 0, issues: [] },
      crawlability: { totalScore: 0, count: 0, issues: [] },
      content: { totalScore: 0, count: 0, issues: [] },
      links: { totalScore: 0, count: 0, issues: [] },
      performance: { totalScore: 0, count: 0, issues: [] },
    };

    let totalOverallScore = 0;
    const allIssues: SeoIssue[] = [];

    for (const page of pagesWithSeo) {
      const seoResult = page.seoResult!;
      totalOverallScore += seoResult.overallScore;

      // Add issues with page URL context
      for (const issue of seoResult.issues) {
        const issueWithPage = {
          ...issue,
          id: `${page.pageUrl}-${issue.id}`,
        };
        allIssues.push(issueWithPage);
        categoryTotals[issue.category].issues.push(issueWithPage);
      }

      // Aggregate category scores
      for (const catScore of seoResult.categoryScores) {
        categoryTotals[catScore.category].totalScore += catScore.score;
        categoryTotals[catScore.category].count++;
      }

      // Merge real Lighthouse SEO audits (they carry category + severity)
      for (const audit of page.lighthouseScores?.seoAudits ?? []) {
        const cat = audit.category as SeoCategory;
        if (!categoryTotals[cat]) continue;
        const issueWithPage: SeoIssue = { ...audit, id: `${page.pageUrl}-lh-${audit.id}` };
        allIssues.push(issueWithPage);
        categoryTotals[cat].issues.push(issueWithPage);
      }
    }

    // Calculate average scores
    const categoryScores: SeoCategoryScore[] = (Object.keys(categoryTotals) as SeoCategory[])
      .filter((cat) => categoryTotals[cat].count > 0)
      .map((category) => {
        const data = categoryTotals[category];
        const avgScore = Math.round(data.totalScore / data.count);
        const issues = data.issues;

        return {
          category,
          score: avgScore,
          issueCount: issues.length,
          criticalCount: issues.filter((i) => i.severity === 'critical').length,
          warningCount: issues.filter((i) => i.severity === 'warning').length,
          infoCount: issues.filter((i) => i.severity === 'info').length,
        };
      })
      .sort((a, b) => a.score - b.score); // Sort by score ascending (worst first)

    const overallScore = Math.round(totalOverallScore / pagesWithSeo.length);

    return {
      overallScore,
      categoryScores,
      allIssues,
      categoryIssues: categoryTotals,
      pagesAnalyzed: pagesWithSeo.length,
    };
  }, [pageResults]);

  if (!aggregatedData) {
    if (loading) {
      return (
        <div className="card text-center py-12">
          <Loader size={48} className="mx-auto text-coral animate-spin mb-4" />
          <p className="text-body text-gray-medium">Loading SEO data…</p>
        </div>
      );
    }
    return <EmptyState />;
  }

  const toggleCategory = (category: SeoCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Filter issues
  const getFilteredIssues = (category: SeoCategory): SeoIssue[] => {
    const issues = aggregatedData.categoryIssues[category].issues;
    if (filterSeverity === 'all') return issues;
    return issues.filter((i) => i.severity === filterSeverity);
  };

  // Summary stats
  const totalCritical = aggregatedData.allIssues.filter((i) => i.severity === 'critical').length;
  const totalWarnings = aggregatedData.allIssues.filter((i) => i.severity === 'warning').length;
  const totalInfo = aggregatedData.allIssues.filter((i) => i.severity === 'info').length;

  return (
    <div className="space-y-8">
      {/* Overview Section */}
      <div className="card">
        <h3 className="text-heading-3 font-semibold mb-6 flex items-center gap-2">
          <Search className="text-coral" size={24} />
          SEO Analysis
        </h3>

        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Score Gauge */}
          <div className="flex flex-col items-center">
            <CircularGauge score={aggregatedData.overallScore} />
            <p className="text-body-sm text-gray-medium mt-3">Overall SEO Score</p>
            <p className="text-caption text-gray-medium">
              {aggregatedData.pagesAnalyzed} page{aggregatedData.pagesAnalyzed !== 1 ? 's' : ''} analyzed
            </p>
          </div>

          {/* Summary Stats */}
          <div className="flex-1 w-full">
            <h4 className="text-body font-semibold mb-3 text-gray-medium">Issue Summary</h4>
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={() => setFilterSeverity(filterSeverity === 'critical' ? 'all' : 'critical')}
                className={`p-4 rounded-card border transition-all ${
                  filterSeverity === 'critical'
                    ? 'bg-status-error/10 border-status-error'
                    : 'bg-status-error/5 border-status-error/20 hover:border-status-error/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle size={18} className="text-status-error" />
                  <span className="text-heading-2 font-bold text-status-error">
                    {totalCritical}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Critical</span>
              </button>
              <button
                onClick={() => setFilterSeverity(filterSeverity === 'warning' ? 'all' : 'warning')}
                className={`p-4 rounded-card border transition-all ${
                  filterSeverity === 'warning'
                    ? 'bg-status-warning/10 border-status-warning'
                    : 'bg-status-warning/5 border-status-warning/20 hover:border-status-warning/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle size={18} className="text-status-warning" />
                  <span className="text-heading-2 font-bold text-status-warning">
                    {totalWarnings}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Warnings</span>
              </button>
              <button
                onClick={() => setFilterSeverity(filterSeverity === 'info' ? 'all' : 'info')}
                className={`p-4 rounded-card border transition-all ${
                  filterSeverity === 'info'
                    ? 'bg-blue-500/10 border-blue-500'
                    : 'bg-blue-500/5 border-blue-500/20 hover:border-blue-500/40'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Info size={18} className="text-blue-500" />
                  <span className="text-heading-2 font-bold text-blue-500">
                    {totalInfo}
                  </span>
                </div>
                <span className="text-body-sm text-gray-medium">Info</span>
              </button>
            </div>
            {filterSeverity !== 'all' && (
              <button
                onClick={() => setFilterSeverity('all')}
                className="mt-3 text-body-sm text-coral hover:underline"
              >
                Clear filter
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <div>
        <h3 className="text-heading-3 font-semibold mb-4 flex items-center gap-2">
          <FileText className="text-coral" size={24} />
          SEO Categories
        </h3>
        <div className="space-y-4">
          {aggregatedData.categoryScores.map((catScore) => (
            <CategoryCard
              key={catScore.category}
              categoryScore={catScore}
              isExpanded={expandedCategories.has(catScore.category)}
              onToggle={() => toggleCategory(catScore.category)}
              issues={getFilteredIssues(catScore.category)}
            />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 text-body-sm">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-success" />
          <span className="text-gray-medium">Excellent (90-100)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-warning" />
          <span className="text-gray-medium">Good (70-89)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-orange-500" />
          <span className="text-gray-medium">Needs Work (50-69)</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-status-error" />
          <span className="text-gray-medium">Poor (0-49)</span>
        </div>
      </div>
    </div>
  );
}
